import React, { useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import { Slide as SlideAnimation } from 'react-awesome-reveal';
import { HeaderV4 } from '../HeaderV4';
import { HeaderV5 } from '../HeaderV5';
import { Button } from '../Button';
import { QuestionAnswerSlideV3 } from '../QuestionAnswerSlideV3';
import { StartSlideV12Experiment } from '../StartSlideV12Experiment';
import { PauseSlide } from '../PauseSlide';
import { ProductSlide } from '../ProductSlide';
import { QuizProgressBarV2 } from '../QuizProgressBarV2';
import { PlanGraphSlideV3 } from '../PlanGraphSlideV3';
import { PlanLoaderSlideV7 } from '../PlanLoaderSlideV7';
import { ReviewSlide } from '../ReviewSlide';
import { EmailSlideV3 } from '../EmailSlideV3';
import { ResultSlide } from '../ResultSlide';
import { SaleV28 } from '../SaleV28';
import { AgreementTextV2 } from '../AgreementTextV2';
import { useQuiz } from '../../shared/use-quiz';
import { useUserProperties } from '../../shared/use-user-properties';
import { useLegalInfo } from '../../shared/use-legal-info';
import { useSlideEvents } from '../../shared/use-slide-events';
import { getAnswersFromSource } from '../../shared/answer-helpers';
import { getPropsFromSourceAnswer } from '../../shared/personalization-helpers';
import {
    EMAIL_SLIDE,
    PAUSE_SLIDE,
    PLAN_GRAPH_SLIDE,
    PLAN_LOADER_SLIDE,
    PRODUCT_SLIDE,
    QUESTION_ANSWER_SLIDE_COMPONENT,
    RESULT_SLIDE,
    REVIEW_SLIDE,
    START_SLIDE
} from '../../shared/quiz-component-types';
import { preloadImages } from '../../../lib/shared/preload-images';
import emojiSpriteSrc from '../../assets/images/emoji-sprites/emoji-sprite-v50.png';
import photosQuestionTabletSrc from '../../assets/images/slide/photos-question-tablet.png';
import digitalMemoirTabletSrc from '../../assets/images/slide/digital-memoir-tablet.png';
import familyMemoirBookSrc from '../../assets/images/slide/family-memoir-book.png';
import styles from './CommonPrelanderV41.css';
import '../../assets/images/emoji-sprites/emoji-sprite-v50.css';

const cx = classnames.bind(styles);

const IMAGES = [emojiSpriteSrc, photosQuestionTabletSrc, digitalMemoirTabletSrc, familyMemoirBookSrc];

const SLIDE_COMPONENTS = {
    [QUESTION_ANSWER_SLIDE_COMPONENT]: QuestionAnswerSlideV3,
    [START_SLIDE]: StartSlideV12Experiment,
    [PAUSE_SLIDE]: PauseSlide,
    [PRODUCT_SLIDE]: ProductSlide,
    [PLAN_GRAPH_SLIDE]: PlanGraphSlideV3,
    [PLAN_LOADER_SLIDE]: PlanLoaderSlideV7,
    [REVIEW_SLIDE]: ReviewSlide,
    [EMAIL_SLIDE]: EmailSlideV3,
    [RESULT_SLIDE]: ResultSlide
};

const DELAY = 350;

function CommonPrelanderV41({
    initialPrelander,
    quizSlides
}) {
    const {
        currentSlide,
        currentSlideIndex,
        results,
        submitAnswer,
        submitTemporaryAnswer,
        temporaryAnswer,
        finished,
        nextSlide,
        backSlide,
        setEmail
    } = useQuiz(quizSlides, initialPrelander);
    const quizSlidesIndexing = quizSlides.filter(slide => !slide.isNotIndexing);
    const progressSlides = quizSlides.filter(slide => slide.hasProgressBar);
    const isQuestionSlide = currentSlide.component === QUESTION_ANSWER_SLIDE_COMPONENT;
    const isStartSlide = currentSlide.component === START_SLIDE;
    const isEmailSlide = currentSlide.component === EMAIL_SLIDE;
    const isDarkTheme = currentSlide.theme === 'dark';
    const numberOfAnsweredSlides = results.userResponses.length + (isQuestionSlide ? 1 : 0);
    const { legalRegion, legalLinks, legalEntity } = useLegalInfo();
    const [selectedSubscriptionData, selectSubscription] = useState({});
    const [merchantData, setMerchantData] = useState({});

    useUserProperties(initialPrelander);

    useEffect(() => {
        preloadImages(IMAGES);
    }, []);

    const { logQuizLifecycle } = useSlideEvents({ currentSlideIndex: numberOfAnsweredSlides, results, currentSlide, quizSlides });

    const handleAnswerWithTransition = answer => () => {
        logQuizLifecycle(quizSlidesIndexing.length === numberOfAnsweredSlides);
        submitAnswer(answer);
    };

    const handleContinue = () => {
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });
        logQuizLifecycle(quizSlidesIndexing.length === numberOfAnsweredSlides);
        nextSlide(temporaryAnswer);
    };

    const renderHeader = () => {
        const hasBackButton = !finished && Boolean(currentSlideIndex) && !currentSlide?.isNotBackButton;
        const HeaderComponent = isDarkTheme ? HeaderV5 : HeaderV4;

        return (
            <HeaderComponent
                hasBackButton={hasBackButton}
                onBack={backSlide}
                className={cx('header', {
                    withBorder: finished || isDarkTheme,
                    dark: isDarkTheme
                })}
                isFinished={false}
                url={window.location.pathname}
            />
        );
    };

    const renderProgressBar = () => {
        if (finished || !currentSlide.hasProgressBar) {
            return null;
        }

        return (
            <div className={cx('progressBarWrapper', { dark: isDarkTheme })}>
                <div className={styles.progressBarInner}>
                    <QuizProgressBarV2
                        currentSlideIndex={progressSlides.findIndex(slide => slide.id === currentSlide.id) + 1}
                        totalSlides={progressSlides.length}
                    />
                </div>
            </div>
        );
    };

    const renderFooter = () => {
        if (finished) {
            return null;
        }

        if (Boolean(currentSlide.continueButton)) {
            return (
                <SlideAnimation
                    delay={500}
                    duration={250}
                    triggerOnce
                    direction='up'
                    className={cx('slideFooter', { dark: isDarkTheme })}
                    key={currentSlide.id}
                >
                    <div className={styles.slideFooterWrapper}>
                        <Button
                            type={isDarkTheme ? 'inkAccent' : 'ink'}
                            label={currentSlide.continueButton.label || 'Continue'}
                            iconRight={{ name: 'chevronRight' }}
                            onClick={handleContinue}
                            inSlide
                        />
                    </div>
                </SlideAnimation>
            );
        }

        return null;
    };

    const renderContent = () => {
        if (finished) {
            return (
                <SaleV28
                    data={results}
                    initialPrelander={initialPrelander}
                    spriteVersion='v50'
                    selectSubscription={selectSubscription}
                    selectedSubscriptionData={selectedSubscriptionData}
                    merchantData={merchantData}
                    setMerchantData={setMerchantData}
                    legalLinks={legalLinks}
                    legalEntity={legalEntity}
                    legalRegion={legalRegion}
                />
            );
        }

        const Slide = SLIDE_COMPONENTS[currentSlide.component];
        const resultData = (temporaryAnswer?.id || temporaryAnswer?.answerIds)
            ? temporaryAnswer
            : results.userResponses.find(userResponse => userResponse.questionId === currentSlide.id);
        const dynamicAnswers = getAnswersFromSource(currentSlide, results);
        const personalizedProps = getPropsFromSourceAnswer(currentSlide, results);

        return (
            <div
                key={currentSlideIndex}
                className={cx('slide', {
                    withQuestion: isQuestionSlide,
                    withStartSlide: isStartSlide,
                    withPlanLoaderSlide: currentSlide.component === PLAN_LOADER_SLIDE,
                    withEmailSlide: isEmailSlide
                })}
            >
                <Slide
                    {...currentSlide.props}
                    {...personalizedProps}
                    answers={dynamicAnswers || currentSlide.props?.answers}
                    spriteVersion='v50'
                    submitAnswer={handleAnswerWithTransition}
                    submitTemporaryAnswer={submitTemporaryAnswer}
                    userResponse={resultData}
                    onContinue={handleContinue}
                    id={currentSlide.id}
                    results={results}
                    delay={DELAY}
                    setEmail={setEmail}
                    legalLinks={legalLinks}
                    legalEntity={legalEntity}
                />
                {isStartSlide && <AgreementTextV2 legalLinks={legalLinks} />}
            </div>
        );
    };

    return (
        <div className={cx('container', { dark: isDarkTheme })}>
            <div className={cx({ stickyHeader: finished && Boolean(results.email) && !selectedSubscriptionData.plan })}>
                {renderHeader()}
                {renderProgressBar()}
            </div>
            <main
                key={finished}
                className={cx('main', {
                    centered: currentSlide.centered,
                    animated: finished || isEmailSlide
                })}
            >
                {renderContent()}
            </main>
            {renderFooter()}
        </div>
    );
}

export {
    CommonPrelanderV41
};
